import React, { useState } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { IChangeEvent } from "@rjsf/core";
import { RJSFSchema } from "@rjsf/utils";
import styles from "../form.module.css";
import img from "../assets/657507_tmpl_head_60db3e2024ea6.png";
import { convertJSONToMultiPartFormData } from "../utils/general";
import { host } from "config";
import FormSubmittedSplashPage from "../formSubmittedSplashPage";

const fileFields = [];

function GroupImplementationSurvey() {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    groupName: "",
    email: "",
    satisfactionInitialCommunication: "",
    satisfactionTraining: "",
    satisfactionFeatures: "",
    likelihoodPortalUsage: "",
    likelihoodRecommendation: "",
    additionalFeedback: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showSubmittedPage, setShowSubmittedPage] = useState(false);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = () => {
    setIsLoading(true);
    fetch(`${host}/api/implementation/forms/groupImplementationSurvey`, {
      method: "POST",
      body: convertJSONToMultiPartFormData(formData, fileFields),
    })
      .then((response) => {
        if (!response.ok) {
          response.text().then((text) => {
            throw new Error(text);
          });
        }
        response.json().then(async (data: any) => {
          setIsLoading(false);

          setShowSubmittedPage(true);
        });
      })
      .catch((error: any) => {
        console.error("Error:", error);
        alert(
          "There was an error while submitting the form. Please try again later and contact IT if the problem persists."
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (isLoading) {
    return (
      <div className="loader-container">
        <CircularProgress />
      </div>
    );
  }

  if (showSubmittedPage) {
    return <FormSubmittedSplashPage formName="Group Implementation Survey" />;
  }

  // Step 0: Welcome page
  if (currentStep === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            backgroundColor: "#ffffff",
            borderRadius: 2,
            border: "2px solid #000",
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#1a365d",
              color: "white",
              py: 2.5,
              px: 3,
              mb: 3,
              textAlign: "center",
              fontSize: "28px",
              fontWeight: 500,
              borderRadius: 2,
            }}
          >
            Group Implementation Survey
          </Box>
          <Box sx={{ mb: 4 }}>
            <img src={img} alt="OptiMed Health" style={{ maxWidth: "200px", height: "auto" }} />
          </Box>

          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="h5" sx={{ color: "#666", fontWeight: 400, lineHeight: 1.8, mb: 6, px: 4 }}>
              Thank you so much for allowing us to provide you training during your Health Plan Implementation with
              OptiMedHealth. Please take a few moments to complete our survey.
            </Typography>

            <Button
              variant="contained"
              onClick={handleNext}
              sx={{
                backgroundColor: "#5a6c7d",
                color: "white",
                px: 4,
                py: 1.5,
                textTransform: "none",
                fontSize: "1rem",
                "&:hover": {
                  backgroundColor: "#4a5c6d",
                },
              }}
            >
              Begin Survey
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Common header for all form pages
  const FormHeader = () => (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 6 }}>
      <img src={img} alt="OptiMed Health" style={{ maxWidth: "200px", height: "auto" }} />
      <Box sx={{ display: "flex", gap: 2 }}>
        {currentStep > 1 && (
          <Button
            onClick={handleBack}
            sx={{
              minWidth: "48px",
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              backgroundColor: "#5a6c7d",
              color: "white",
              "&:hover": {
                backgroundColor: "#4a5c6d",
              },
            }}
          >
            <ArrowBackIcon />
          </Button>
        )}
        {currentStep < 6 && (
          <Button
            onClick={handleNext}
            sx={{
              minWidth: "48px",
              width: "48px",
              height: "48px",
              borderRadius: "50%",
              backgroundColor: "#5a6c7d",
              color: "white",
              "&:hover": {
                backgroundColor: "#4a5c6d",
              },
            }}
          >
            <ArrowForwardIcon />
          </Button>
        )}
      </Box>
    </Box>
  );

  // Step 1: Your name (First Name + Last Name side by side)
  if (currentStep === 1) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            backgroundColor: "#ffffff",
            borderRadius: 2,
            border: "2px solid #000",
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#1a365d",
              color: "white",
              py: 2.5,
              px: 3,
              mb: 3,
              textAlign: "center",
              fontSize: "28px",
              fontWeight: 500,
              borderRadius: 2,
            }}
          >
            Group Implementation Survey
          </Box>
          <FormHeader />

          <Box sx={{ maxWidth: "800px" }}>
            <Typography variant="body1" sx={{ mb: 1, color: "#424242", fontWeight: 500 }}>
              Your name
            </Typography>

            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mb: 2 }}>
              <Box>
                <TextField
                  fullWidth
                  value={formData.firstName}
                  onChange={(e) => handleChange("firstName", e.target.value)}
                  variant="outlined"
                  size="small"
                />
                <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "#424242" }}>
                  First Name
                </Typography>
              </Box>

              <Box>
                <TextField
                  fullWidth
                  value={formData.lastName}
                  onChange={(e) => handleChange("lastName", e.target.value)}
                  variant="outlined"
                  size="small"
                />
                <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "#424242" }}>
                  Last Name
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Step 2: Your Group Name
  if (currentStep === 2) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            backgroundColor: "#ffffff",
            borderRadius: 2,
            border: "2px solid #000",
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#1a365d",
              color: "white",
              py: 2.5,
              px: 3,
              mb: 3,
              textAlign: "center",
              fontSize: "28px",
              fontWeight: 500,
              borderRadius: 2,
            }}
          >
            Group Implementation Survey
          </Box>
          <FormHeader />

          <Box sx={{ maxWidth: "800px" }}>
            <Typography variant="body1" sx={{ mb: 1, color: "#424242", fontWeight: 500 }}>
              Your Group Name
            </Typography>

            <TextField
              fullWidth
              value={formData.groupName}
              onChange={(e) => handleChange("groupName", e.target.value)}
              variant="outlined"
              size="small"
            />
          </Box>
        </Paper>
      </Container>
    );
  }

  // Step 3: Email
  if (currentStep === 3) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            backgroundColor: "#ffffff",
            borderRadius: 2,
            border: "2px solid #000",
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#1a365d",
              color: "white",
              py: 2.5,
              px: 3,
              mb: 3,
              textAlign: "center",
              fontSize: "28px",
              fontWeight: 500,
              borderRadius: 2,
            }}
          >
            Group Implementation Survey
          </Box>
          <FormHeader />

          <Box sx={{ maxWidth: "800px" }}>
            <Typography variant="body1" sx={{ mb: 1, color: "#000", fontWeight: 500 }}>
              Email
            </Typography>

            <TextField
              fullWidth
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              variant="outlined"
              size="small"
            />
          </Box>
        </Paper>
      </Container>
    );
  }

  // Step 4: Satisfaction matrix
  if (currentStep === 4) {
    const satisfactionOptions = [
      { value: "very_satisfied", label: "Very Satisfied" },
      { value: "satisfied", label: "Satisfied" },
      { value: "neutral", label: "Neutral" },
      { value: "unsatisfied", label: "Unsatisfied" },
      { value: "very_unsatisfied", label: "Very Unsatisfied" },
    ];

    const questions = [
      { field: "satisfactionInitialCommunication", text: "The initial communication process to setup your training" },
      { field: "satisfactionTraining", text: "The training provided by our team" },
      { field: "satisfactionFeatures", text: "Features/options available in our employer portal" },
    ];

    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            backgroundColor: "#ffffff",
            borderRadius: 2,
            border: "2px solid #000",
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#1a365d",
              color: "white",
              py: 2.5,
              px: 3,
              mb: 3,
              textAlign: "center",
              fontSize: "28px",
              fontWeight: 500,
              borderRadius: 2,
            }}
          >
            Group Implementation Survey
          </Box>
          <FormHeader />

          <Box sx={{ maxWidth: "1200px" }}>
            <Typography variant="body1" sx={{ mb: 3, color: "#666", fontWeight: 500 }}>
              How satisfied are you with...
            </Typography>

            <Box
              sx={{
                border: "1px solid #ddd",
                "& > *:not(:last-child)": {
                  borderBottom: "1px solid #ddd",
                },
              }}
            >
              {/* Header row */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "300px repeat(5, 1fr)",
                  backgroundColor: "#f5f5f5",
                  borderBottom: "1px solid #ddd",
                }}
              >
                <Box sx={{ p: 2 }}></Box>
                {satisfactionOptions.map((option) => (
                  <Box key={option.value} sx={{ p: 2, textAlign: "center", fontWeight: 500, fontSize: "0.9rem" }}>
                    {option.label}
                  </Box>
                ))}
              </Box>

              {/* Question rows */}
              {questions.map((question, idx) => (
                <Box
                  key={question.field}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "300px repeat(5, 1fr)",
                    "&:hover": { backgroundColor: "#fafafa" },
                  }}
                >
                  <Box sx={{ p: 2, display: "flex", alignItems: "center", fontSize: "0.9rem" }}>{question.text}</Box>
                  {satisfactionOptions.map((option) => (
                    <Box
                      key={option.value}
                      sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      <Radio
                        checked={formData[question.field] === option.value}
                        onChange={() => handleChange(question.field, option.value)}
                        value={option.value}
                        sx={{
                          color: "#5a6c7d",
                          "&.Mui-checked": {
                            color: "#5a6c7d",
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Step 5: Likelihood matrix
  if (currentStep === 5) {
    const likelihoodOptions = [
      { value: "very_likely", label: "Very Likely" },
      { value: "likely", label: "Likely" },
      { value: "somewhat_likely", label: "Somewhat Likely" },
      { value: "unlikely", label: "UnLikely" },
      { value: "very_unlikely", label: "Very Unlikely" },
    ];

    const questions = [
      { field: "likelihoodPortalUsage", text: "Utilize our employer portal to administer your plan with us" },
      { field: "likelihoodRecommendation", text: "Recommend us to a peer" },
    ];

    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            backgroundColor: "#ffffff",
            borderRadius: 2,
            border: "2px solid #000",
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#1a365d",
              color: "white",
              py: 2.5,
              px: 3,
              mb: 3,
              textAlign: "center",
              fontSize: "28px",
              fontWeight: 500,
              borderRadius: 2,
            }}
          >
            Group Implementation Survey
          </Box>
          <FormHeader />

          <Box sx={{ maxWidth: "1200px" }}>
            <Typography variant="body1" sx={{ mb: 3, color: "#666", fontWeight: 500 }}>
              How likely are you to...
            </Typography>

            <Box
              sx={{
                border: "1px solid #ddd",
                "& > *:not(:last-child)": {
                  borderBottom: "1px solid #ddd",
                },
              }}
            >
              {/* Header row */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "300px repeat(5, 1fr)",
                  backgroundColor: "#f5f5f5",
                  borderBottom: "1px solid #ddd",
                }}
              >
                <Box sx={{ p: 2 }}></Box>
                {likelihoodOptions.map((option) => (
                  <Box key={option.value} sx={{ p: 2, textAlign: "center", fontWeight: 500, fontSize: "0.9rem" }}>
                    {option.label}
                  </Box>
                ))}
              </Box>

              {/* Question rows */}
              {questions.map((question) => (
                <Box
                  key={question.field}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "300px repeat(5, 1fr)",
                    "&:hover": { backgroundColor: "#fafafa" },
                  }}
                >
                  <Box sx={{ p: 2, display: "flex", alignItems: "center", fontSize: "0.9rem" }}>{question.text}</Box>
                  {likelihoodOptions.map((option) => (
                    <Box
                      key={option.value}
                      sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      <Radio
                        checked={formData[question.field] === option.value}
                        onChange={() => handleChange(question.field, option.value)}
                        value={option.value}
                        sx={{
                          color: "#5a6c7d",
                          "&.Mui-checked": {
                            color: "#5a6c7d",
                          },
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Paper>
      </Container>
    );
  }

  // Step 6: Additional feedback
  if (currentStep === 6) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            backgroundColor: "#ffffff",
            borderRadius: 2,
            border: "2px solid #000",
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#1a365d",
              color: "white",
              py: 2.5,
              px: 3,
              mb: 3,
              textAlign: "center",
              fontSize: "28px",
              fontWeight: 500,
              borderRadius: 2,
            }}
          >
            Group Implementation Survey
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 6 }}>
            <img src={img} alt="OptiMed Health" style={{ maxWidth: "200px", height: "auto" }} />
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                onClick={handleBack}
                sx={{
                  minWidth: "48px",
                  width: "48px",
                  height: "48px",
                  borderRadius: "50%",
                  backgroundColor: "#5a6c7d",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "#4a5c6d",
                  },
                }}
              >
                <ArrowBackIcon />
              </Button>
              <Button
                onClick={handleSubmit}
                sx={{
                  px: 4,
                  py: 1.5,
                  backgroundColor: "#5a6c7d",
                  color: "white",
                  textTransform: "none",
                  fontSize: "1rem",
                  "&:hover": {
                    backgroundColor: "#4a5c6d",
                  },
                }}
              >
                Submit
              </Button>
            </Box>
          </Box>

          <Box sx={{ maxWidth: "1200px" }}>
            <Typography variant="body1" sx={{ mb: 2, color: "#666", fontWeight: 400 }}>
              Please provide any additional feedback in order for us to improve our group implementation process
            </Typography>

            <TextField
              fullWidth
              multiline
              rows={10}
              value={formData.additionalFeedback}
              onChange={(e) => handleChange("additionalFeedback", e.target.value)}
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#3399ff",
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": {
                    borderColor: "#3399ff",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#3399ff",
                  },
                },
              }}
            />
          </Box>
        </Paper>
      </Container>
    );
  }

  return null;
}

export default GroupImplementationSurvey;
